'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Map, X, Waves, Droplets, TreePine } from 'lucide-react';
import type { Region, RegionType } from '@/hooks/useRegions';

interface QuickCompareRegionsProps {
  regions: Region[];
  type: RegionType;
}

// 获取类型图标
const getTypeIcon = (type: RegionType) => {
  switch (type) {
    case 'ocean':
      return Waves;
    case 'lake':
      return Droplets;
    case 'island':
      return TreePine;
    default:
      return Map;
  }
};

// 获取类型名称
const getTypeName = (type: RegionType) => {
  switch (type) {
    case 'ocean':
      return 'Oceans';
    case 'lake':
      return 'Lakes';
    case 'island':
      return 'Islands';
    default:
      return 'Regions';
  }
};

export default function QuickCompareRegions({ regions, type }: QuickCompareRegionsProps) {
  const router = useRouter();
  const [selectedRegions, setSelectedRegions] = useState<(Region | undefined)[]>([undefined, undefined]);

  const handleRegionSelect = (region: Region, index: number) => {
    setSelectedRegions(prev => {
      const newSelection = [...prev];
      newSelection[index] = region;
      return newSelection;
    });
  };

  const handleRemoveRegion = (index: number) => {
    setSelectedRegions(prev => {
      const newSelection = [...prev];
      newSelection[index] = undefined as any;
      return newSelection;
    });
  };

  const handleCompare = () => {
    if (selectedRegions[0] && selectedRegions[1]) {
      const url = `/map?a=${selectedRegions[0].slug}&b=${selectedRegions[1].slug}`;
      router.push(url);
    }
  };

  const canCompare = selectedRegions[0] && selectedRegions[1];
  const TypeIcon = getTypeIcon(type);
  const typeName = getTypeName(type);

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <TypeIcon className="h-4 w-4 text-blue-600" />
        <h2 className="text-base font-semibold text-slate-900">Quick Compare {typeName}</h2>
      </div>
      
      <p className="text-slate-600 mb-4 text-sm">
        Select two {type}s to compare their true sizes on the interactive map.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        {/* 第一个区域选择 */}
        <div className="relative">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            First {typeName.slice(0, -1)}
          </label>
          <select
            value={selectedRegions[0]?.id || ''}
            onChange={(e) => {
              const region = regions.find(r => r.id === e.target.value);
              if (region) handleRegionSelect(region, 0);
            }}
            className="w-full p-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select {typeName.slice(0, -1).toLowerCase()}...</option>
            {regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
          {selectedRegions[0] && (
            <div className="mt-2 flex items-center gap-2 p-2 bg-blue-50 rounded-md">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: selectedRegions[0].color }}
              />
              <span className="text-sm text-slate-700 flex-1">{selectedRegions[0].name}</span>
              <button
                onClick={() => handleRemoveRegion(0)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        {/* 第二个区域选择 */}
        <div className="relative">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Second {typeName.slice(0, -1)}
          </label>
          <select
            value={selectedRegions[1]?.id || ''}
            onChange={(e) => {
              const region = regions.find(r => r.id === e.target.value);
              if (region) handleRegionSelect(region, 1);
            }}
            className="w-full p-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select {typeName.slice(0, -1).toLowerCase()}...</option>
            {regions.map((region) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
          {selectedRegions[1] && (
            <div className="mt-2 flex items-center gap-2 p-2 bg-blue-50 rounded-md">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: selectedRegions[1].color }}
              />
              <span className="text-sm text-slate-700 flex-1">{selectedRegions[1].name}</span>
              <button
                onClick={() => handleRemoveRegion(1)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={handleCompare}
        disabled={!canCompare}
        className={`w-full py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
          canCompare
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
        }`}
      >
        <Map className="h-4 w-4" />
        Compare on Map
      </button>
    </div>
  );
}
